// 開發者帳戶分級管理面板(common/08 §3.1.1;業主定案 2026-07-12)。
// 平台 admin 以 email 查一個開發者帳戶,檢視 tier + model 上限,並執行兩個
// 後台操作:調整機型數上限 / 升級為企業。個人自助開通不進審核佇列,故此處
// 以 email 直查(fam-devmgmt 軸)。兩動作皆需原因 + 案號,對應 LIVE 端點。
import { useState } from 'react';
import {
  DeveloperAccount,
  lookupDeveloper, setModelQuota, upgradeEnterprise,
} from './devReviewApi';

const TIER_LABEL: Record<string, string> = { individual: '個人', enterprise: '企業' };

export default function DeveloperAccountPanel({ writable }: { writable: boolean }) {
  const [email, setEmail] = useState('');
  const [account, setAccount] = useState<DeveloperAccount | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [quota, setQuota] = useState('');
  const [quotaReason, setQuotaReason] = useState('');
  const [upgradeReason, setUpgradeReason] = useState('');

  const flash = (msg: string) => { setNotice(msg); setErr(null); };
  const fail = (e: unknown, fallback: string) => setErr(e instanceof Error ? e.message : fallback);

  const lookup = async () => {
    const q = email.trim();
    if (!q) return;
    setBusy('lookup'); setErr(null); setNotice(null); setAccount(null);
    try {
      const acc = await lookupDeveloper(q);
      setAccount(acc);
      setQuota(String(acc.model_quota));
    } catch (e) {
      fail(e, '查無此開發者帳戶（或該 email 不是開發者）');
    } finally {
      setBusy(null);
    }
  };

  const refresh = async (acc: DeveloperAccount) => {
    try {
      const next = await lookupDeveloper(acc.email || email.trim());
      setAccount(next);
      setQuota(String(next.model_quota));
    } catch { /* keep prior view */ }
  };

  const applyQuota = async () => {
    if (!account) return;
    const n = Number(quota);
    if (!Number.isInteger(n) || n < 1) { setErr('model 上限必須是 ≥ 1 的整數'); return; }
    if (!quotaReason.trim()) { setErr('請填調整原因'); return; }
    setBusy('quota'); setErr(null); setNotice(null);
    try {
      await setModelQuota(account.user_id, n, quotaReason.trim(), 'admin-console');
      flash(`已將 ${account.email || account.user_id} 的 model 上限設為 ${n}`);
      setQuotaReason('');
      await refresh(account);
    } catch (e) {
      fail(e, '調整機型數上限失敗');
    } finally {
      setBusy(null);
    }
  };

  const doUpgrade = async () => {
    if (!account) return;
    if (!upgradeReason.trim()) { setErr('請填升級原因'); return; }
    setBusy('upgrade'); setErr(null); setNotice(null);
    try {
      await upgradeEnterprise(account.user_id, upgradeReason.trim(), 'admin-console');
      flash(`已將 ${account.email || account.user_id} 升級為企業帳戶`);
      setUpgradeReason('');
      await refresh(account);
    } catch (e) {
      fail(e, '升級為企業失敗');
    } finally {
      setBusy(null);
    }
  };

  const isEnterprise = account?.account_tier === 'enterprise';

  return (
    <section className="card dr-account-card">
      <div className="section-head">
        <h2 className="section-title">開發者帳戶管理</h2>
        <span className="section-meta">依電子郵件查詢 · 調機型數上限 / 升級企業</span>
      </div>

      <div className="dr-account-lookup">
        <input
          type="email" value={email} placeholder="developer@company.com"
          aria-label="開發者 email"
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void lookup(); }}
        />
        <button onClick={() => void lookup()} disabled={busy === 'lookup' || !email.trim()}>
          {busy === 'lookup' ? '查詢中…' : '查詢'}
        </button>
      </div>

      {notice && <div className="adm-action-result is-ok" role="status">{notice}</div>}
      {err && <div className="adm-action-result is-err" role="alert">{err}</div>}

      {account && (
        <div className="dr-account-detail">
          <div className="dr-account-head">
            <div>
              <p className="dr-account-email">{account.email || account.user_id}</p>
              <p className="text-secondary dr-account-sub">
                {account.slug ? `工作區 ${account.slug} · ` : ''}狀態 {account.state}
              </p>
            </div>
            <span className={`pill ${isEnterprise ? 'pill-good' : 'pill-neutral'}`}>
              {TIER_LABEL[account.account_tier] ?? account.account_tier}
            </span>
          </div>

          <div className="dr-account-metrics">
            <div className="metric-box">
              <span className="metric-label">機型數上限</span>
              <span className="metric-value">{account.model_quota}</span>
            </div>
            <div className="metric-box">
              <span className="metric-label">上限來源</span>
              <span className="metric-value dr-account-src">
                {account.quota_override ? '個別覆寫' : (isEnterprise ? '企業預設 50' : '個人預設 3')}
              </span>
            </div>
          </div>

          {writable ? (
            <>
              <div className="dr-account-action">
                <label className="dr-account-action-label" htmlFor="dr-quota">調整機型數上限</label>
                <div className="dr-account-row">
                  <input id="dr-quota" type="number" min={1} value={quota}
                    onChange={(e) => setQuota(e.target.value)} aria-label="新的機型數上限" />
                  <input type="text" value={quotaReason} placeholder="原因（必填）"
                    onChange={(e) => setQuotaReason(e.target.value)} aria-label="調整原因" />
                  <button onClick={() => void applyQuota()} disabled={busy === 'quota'}>
                    {busy === 'quota' ? '套用中…' : '套用'}
                  </button>
                </div>
              </div>

              <div className="dr-account-action">
                <div className="dr-account-upgrade-head">
                  <div>
                    <p className="dr-account-action-label">升級為企業帳戶</p>
                    <p className="text-muted dr-account-hint">
                      {isEnterprise ? '此帳戶已是企業。' : '升級後可建立 org（建立時仍需企業信箱）。'}
                    </p>
                  </div>
                </div>
                {!isEnterprise && (
                  <div className="dr-account-row">
                    <input type="text" value={upgradeReason} placeholder="升級原因（必填）"
                      onChange={(e) => setUpgradeReason(e.target.value)} aria-label="升級原因" />
                    <button className="dr-account-upgrade-btn" onClick={() => void doUpgrade()} disabled={busy === 'upgrade'}>
                      {busy === 'upgrade' ? '升級中…' : '升級為企業'}
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="text-muted dr-account-hint">唯讀角色：僅可檢視,不能調整配額或升級。</p>
          )}
        </div>
      )}
    </section>
  );
}
